// @ts-nocheck
/**
 * OOTW Subscribe & Save selector.
 *
 * A self-contained custom element that drives a Lineage-style subscription /
 * one-time purchase selector using the store's Skio selling plans.
 *
 * Responsibilities:
 *  - Toggle between Subscribe & Save and Try Once.
 *  - Keep the hidden `name="selling_plan"` input (inside the product form) in
 *    sync: the selected plan id when subscribing, empty when one-time.
 *  - Rebuild the frequency dropdown and prices when the variant changes.
 *
 * It reads all data from a JSON blob rendered by Liquid, so no Skio API key or
 * hardcoded plan ids are needed. Variant changes are observed via the theme's
 * bubbling `variant:update` event.
 */
class OotwSubscribeSave extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.dataModel = this.#parseData();
    this.defaultInterval = parseInt(this.dataset.defaultInterval || '28', 10) || 28;

    this.refs = {
      input: this.querySelector('[data-ootw-selling-plan]'),
      cards: this.querySelector('[data-ootw-cards]'),
      subCard: this.querySelector('[data-ootw-option="subscription"]'),
      onceCard: this.querySelector('[data-ootw-option="onetime"]'),
      subPrice: this.querySelector('[data-ootw-sub-price]'),
      subCompare: this.querySelector('[data-ootw-sub-compare]'),
      oncePrice: this.querySelector('[data-ootw-once-price]'),
      select: this.querySelector('[data-ootw-frequency]'),
    };

    this.selected = 'subscription';
    this.currentVariantId = this.#readFormVariantId() || this.#firstVariantId();

    this.#bindEvents();
    this.render();
  }

  // ---- data ---------------------------------------------------------------

  #parseData() {
    const el = this.querySelector('[data-ootw-data]');
    if (!el) return { variants: {} };
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.warn('[ootw-subscribe-save] failed to parse data', err);
      return { variants: {} };
    }
  }

  #firstVariantId() {
    const keys = Object.keys(this.dataModel.variants || {});
    return keys.length ? keys[0] : null;
  }

  get #form() {
    return this.closest('form') || this.closest('product-form-component')?.querySelector('form');
  }

  #readFormVariantId() {
    const form = this.#form;
    const input = form?.querySelector('input[name="id"], select[name="id"]');
    return input?.value || null;
  }

  #variant() {
    const id = this.currentVariantId != null ? String(this.currentVariantId) : '';
    return this.dataModel.variants?.[id] || null;
  }

  // ---- events -------------------------------------------------------------

  #bindEvents() {
    this.querySelectorAll('[data-ootw-select]').forEach((btn) => {
      btn.addEventListener('click', () => this.#selectOption(btn.dataset.ootwSelect));
    });

    this.refs.select?.addEventListener('change', () => {
      if (this.selected === 'subscription') this.#writeInput(this.refs.select.value);
    });

    // The theme dispatches `variant:update` on the section and it bubbles to document.
    const productId = this.dataset.productId;
    document.addEventListener('variant:update', (event) => {
      const detail = event.detail || {};
      if (detail.data?.productId && productId && detail.data.productId !== productId) return;
      const newId = detail.resource?.id;
      this.currentVariantId = newId != null ? String(newId) : this.#readFormVariantId();
      this.render();
    });
  }

  #selectOption(option) {
    if (option === 'onetime' && this.#variant()?.requiresPlan) return; // can't go one-time
    this.selected = option === 'onetime' ? 'onetime' : 'subscription';
    this.render();
  }

  // ---- rendering ----------------------------------------------------------

  render() {
    const variant = this.#variant();
    const plans = variant?.plans || [];

    // No subscription available for this variant -> force one-time, hide sub card.
    if (!variant || plans.length === 0) {
      this.selected = 'onetime';
      this.#writeInput('');
      this.refs.subCard?.style.setProperty('display', 'none');
      this.#applyActiveState();
      if (variant) this.#renderOneTimePrice(variant);
      this.setAttribute('data-selected', this.selected);
      return;
    }
    this.refs.subCard?.style.removeProperty('display');

    // Subscription-only variant -> force subscription, hide one-time card.
    if (variant.requiresPlan) {
      this.selected = 'subscription';
      this.refs.onceCard?.style.setProperty('display', 'none');
      this.refs.cards?.setAttribute('data-subscription-only', '');
    } else {
      this.refs.onceCard?.style.removeProperty('display');
      this.refs.cards?.removeAttribute('data-subscription-only');
      this.#renderOneTimePrice(variant);
    }

    this.#renderFrequencyOptions(variant, plans);

    const activePlan = this.#activePlan(plans);
    this.#renderSubscriptionPrice(variant, activePlan);

    if (this.selected === 'subscription') {
      this.#writeInput(activePlan ? activePlan.id : '');
    } else {
      this.#writeInput('');
    }

    this.#applyActiveState();
    this.setAttribute('data-selected', this.selected);
  }

  #activePlan(plans) {
    // Preserve the currently chosen plan id if it still exists for this variant.
    const chosen = this.refs.select?.value;
    let plan = plans.find((p) => String(p.id) === String(chosen));
    if (plan) return plan;

    // Otherwise match the preferred default interval (e.g. 28).
    plan = plans.find((p) => this.#intervalNumber(p) === this.defaultInterval);
    return plan || plans[0];
  }

  #renderFrequencyOptions(variant, plans) {
    const select = this.refs.select;
    if (!select) return;

    const previous = select.value;
    const active = this.#activePlan(plans);
    select.innerHTML = '';

    plans.forEach((plan) => {
      const opt = document.createElement('option');
      opt.value = String(plan.id);
      opt.textContent = this.#intervalLabel(plan);
      if (active && String(plan.id) === String(active.id)) opt.selected = true;
      select.appendChild(opt);
    });

    // Restore previous if it survived the rebuild.
    if (previous && plans.some((p) => String(p.id) === String(previous))) {
      select.value = previous;
    }
  }

  #renderSubscriptionPrice(variant, plan) {
    if (!plan) return;

    if (this.refs.subPrice) this.refs.subPrice.textContent = plan.price;

    const compareText = plan.compareAt || variant.oneTime;
    const showCompare = plan.priceAmount != null && plan.compareAtAmount != null
      ? plan.compareAtAmount > plan.priceAmount
      : true;
    if (this.refs.subCompare) this.refs.subCompare.textContent = showCompare ? compareText : '';
  }

  #renderOneTimePrice(variant) {
    if (this.refs.oncePrice) this.refs.oncePrice.textContent = variant.oneTime;
  }

  #applyActiveState() {
    const sub = this.refs.subCard;
    const once = this.refs.onceCard;
    const isSub = this.selected === 'subscription';

    sub?.classList.toggle('is-active', isSub);
    once?.classList.toggle('is-active', !isSub);

    sub?.querySelector('[data-ootw-select]')?.setAttribute('aria-pressed', String(isSub));
    once?.setAttribute('aria-pressed', String(!isSub));
  }

  #writeInput(value) {
    if (this.refs.input) this.refs.input.value = value == null ? '' : String(value);
  }

  // ---- helpers ------------------------------------------------------------

  #intervalNumber(plan) {
    const source = plan.optionValue || plan.name || '';
    const match = String(source).match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
  }

  #intervalLabel(plan) {
    const n = this.#intervalNumber(plan);
    if (n != null) return `${n} Days`;
    return plan.optionValue || plan.name || '';
  }
}

if (!customElements.get('ootw-subscribe-save')) {
  customElements.define('ootw-subscribe-save', OotwSubscribeSave);
}
